import { Button } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { IBestDataset } from '../../../../../../types/analytics';
import { IScatterTableApiPayload } from '../../../../../../types/chart';
import { MarkerItem } from '../../../../../../types/home';
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput';
import { IEnrichedMetadata } from '../../../../../../types/metadata';
import { useScatterTableParams } from '../../../hooks/useScatterTableParams';
import { AnalyticsSection } from '../../Layout/AnalyticsSection';
import ScatterTableAdditionalInput from './ScatterTableAdditionalInput';
import ScatterTableChart from './ScatterTableChart';
import ScatterTableInputForm from './ScatterTableInputForm';

interface ScatterTableProps {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[];
  onDatasetChange: (item: string) => void;
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IScatterTableApiPayload) => void;
  error: string;
  data: any[];
  dateRange: [moment.Moment, moment.Moment];
  scatterTableLinks: string[];
  pointName: string;
  userInput: IAnalytcisUserInput;
  gridCellSize: string;
  waterDepth: string;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  onChangeActiveKey: (item: any) => void;
  pointOption: { label: string; value: string }[];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  updating: boolean;
  isOfflineDataset: boolean;
}

export default function ScatterTable(props: ScatterTableProps) {
  const { updating, error, scatterTableLinks, pointName, isOfflineDataset } =
    props;

  const {
    dataset,
    datasetY,
    variableX,
    variableY,
    tempVariableX,
    tempVariableY,
    month,
    intervalX,
    intervalY,
    tableData,
    column,
    variablesX,
    variablesY,
    isChangeDatasetY,
    startDate,
    endDate,
    categories,
    onUpdateData,
    doMonthChange,
    doVariableChangeX,
    doVariableChangeY,
    doChangeIntervalX,
    doChangeIntervalY,
    doDatasetYChange,
    doStartDateChange,
    doEndDateChange,
    onselectDatasetX,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  } = useScatterTableParams(props);

  // TODO: enable cache
  // const upsertScatterTableCache = useAnalyticsCacheStore(state => state.upsertScatterTableCache)

  // useEffect(() => {
  //   if (!props.previewDatasetId || !props.coordinates || !variableX?.Index) return;

  //   const key = `${props.previewDatasetId}_${variableX.Index}_${props.coordinates[0]},${props.coordinates[1]}`;
  //   upsertScatterTableCache(key, {
  //     variableXIndex: variableX.Index,
  //     tableData: props.data,
  //     column,
  //     startDate,
  //     endDate,
  //     isForAllAnalytics: props.isForAllAnalytics,
  //     variableX,
  //     variableY,
  //     scatterTableLinks,
  //   });

  // }, [upsertScatterTableCache, props.previewDatasetId, props.coordinates, variableX?.Index, variableY, props.isForAllAnalytics, startDate, endDate, props.data, variableX, variableY, scatterTableLinks]);

  const plotInputContent = useMemo(
    () => (
      <ScatterTableInputForm
        datasets={props.categoryDataset}
        previewDatasetId={props.previewDatasetId}
        onDatasetChange={onselectDatasetX}
        categories={categories}
        variables={variablesX}
        tempVariable={tempVariableX}
        onVariableChange={doVariableChangeX}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={doStartDateChange}
        onEndDateChange={doEndDateChange}
        disabledStartDate={disabledStartDate}
        disabledEndDate={disabledEndDate}
        isForAllAnalytics={props.isForAllAnalytics}
        onCheckboxChange={handleCheckboxChange}
        isLoading={updating}
        hasError={!!error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      props.categoryDataset,
      props.previewDatasetId,
      onselectDatasetX,
      categories,
      variablesX,
      dataset,
      tempVariableX,
      doVariableChangeX,
      startDate,
      endDate,
      doStartDateChange,
      doEndDateChange,
      disabledStartDate,
      disabledEndDate,
      props.isForAllAnalytics,
      handleCheckboxChange,
      updating,
      error,
      isOfflineDataset,
    ]
  );

  const updateContent = useMemo(
    () => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '36px',
        }}
      >
        <Button
          type='primary'
          data-testid='Analytics-Update_Button'
          disabled={updating || isOfflineDataset}
          loading={updating}
          onClick={() => onUpdateData()}
          style={{
            width: '90px',
            height: '36px',
          }}
        >
          Update
        </Button>
      </div>
    ),
    [updating, onUpdateData, isOfflineDataset]
  );

  const chartContent = useMemo(
    () => (
      <ScatterTableChart
        tableData={tableData}
        column={column}
        pointName={pointName}
        variableX={variableX}
        variableY={variableY}
        scatterTableLinks={scatterTableLinks}
        isLoading={updating}
        error={error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      tableData,
      column,
      pointName,
      variableX,
      variableY,
      scatterTableLinks,
      updating,
      error,
      isOfflineDataset,
    ]
  );

  const additionalContent = useMemo(
    () => (
      <ScatterTableAdditionalInput
        datasets={props.categoryDataset}
        categories={categories}
        datasetY={datasetY}
        onDatasetYChange={doDatasetYChange}
        isChangeDatasetY={isChangeDatasetY}
        variablesY={variablesY}
        tempVariableY={tempVariableY}
        onVariableYChange={doVariableChangeY}
        intervalX={intervalX}
        onChangeIntervalX={doChangeIntervalX}
        intervalY={intervalY}
        onChangeIntervalY={doChangeIntervalY}
        month={month}
        onMonthChange={doMonthChange}
        isLoading={updating}
        hasError={!!error}
        isOfflineDataset={isOfflineDataset}
      />
    ),
    [
      props.categoryDataset,
      categories,
      datasetY,
      doDatasetYChange,
      isChangeDatasetY,
      variablesY,
      tempVariableY,
      doVariableChangeY,
      intervalX,
      doChangeIntervalX,
      intervalY,
      doChangeIntervalY,
      month,
      doMonthChange,
      updating,
      error,
      isOfflineDataset,
    ]
  );

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={additionalContent}
      isLoading={updating}
    />
  );
}
